import DataTableGrid from '@bumble/app/components/DataTableGrid';
import DialogStandard from '@bumble/app/components/DialogStandard';
import DisplayContacts from '@bumble/app/components/DisplayContacts';
import { Indicator } from '@bumble/app/components/Indicator';
import { Loading } from '@bumble/app/components/Loading';
import NoData from '@bumble/app/components/NoData';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import PermContactCalendarOutlinedIcon from '@mui/icons-material/PermContactCalendarOutlined';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { useMutation, useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

const columns: GridColDef[] = [
  {
    field: 'ep_name',
    headerName: 'Endpoint Name',
    flex: 1,
    minWidth: 200,
    disableColumnMenu: true,
  },
  {
    field: 'ep_id',
    headerName: 'Endpoint ID',
    width: 200,
    disableColumnMenu: true,
  },
  // {
  //  field: 'ep_node_id',
  //  headerName: 'EP Node Id',
  //  width: 200,
  //  disableColumnMenu: true,
  // },
  {
    field: 'account_id',
    headerName: 'Endpoint Owner Account',
    width: 200,
    disableColumnMenu: true,
  },
  {
    field: 'admin_state',
    headerName: 'Admin State',
    width: 200,
    disableColumnMenu: true,
  },
  {
    field: 'op_state',
    headerName: 'Op State',
    width: 200,
    disableColumnMenu: true,
    renderCell: (params) => {
      return params.value !== undefined ? <Indicator active={params.value === 'up'} /> : 'N/A';
    }
  },
  {
    field: 'fqdn',
    headerName: 'App DNS Name',
    width: 500,
    disableColumnMenu: true,
  },
  {
    field: 'bgp_advertised_routes',
    headerName: 'BGP Advertised Routes',
    width: 500,
    disableColumnMenu: true,
  },
  {
    field: 'hosting_id',
    headerName: 'Hosting ID',
    width: 200,
    disableColumnMenu: true,
  },
  {
    field: 'ip_addr',
    headerName: 'EP IP Address',
    width: 150,
    disableColumnMenu: true,
  },
  // {
  //  field: 'tx_bytes',
  //  headerName: 'Tx Bytes',
  //  width: 150,
  //  disableColumnMenu: true,
  // },
  // {
  //  field: 'rx_bytes',
  //  headerName: 'Rx Bytes',
  //  width: 150,
  //  disableColumnMenu: true,
  // },
  {
    field: 'created_at',
    headerName: 'EP Created at',
    minWidth: 150,
    headerAlign: 'right',
    align: 'right',
    disableColumnMenu: true,
    valueGetter: (params) => {
      if (params.value === null) return '';
      return DateTime.fromISO(params.value).toLocaleString(DateTime.DATETIME_SHORT);
    },
  },
  // {
  //   field: 'type',
  //   headerName: 'Type',
  //   width: 200,
  //   align: 'right',
  //   disableColumnMenu: true,
  // },
];

const columnVisible = {
  ep_name: true,
  ep_id: true,
  hosting_id: true,
  // ep_node_id: true,
  account_id: true,
  admin_state: true,
  op_state: true,
  fqdn: true,
  ip_addr: true,
  tx_bytes: true,
  rx_bytes: true,
  created_at: true,
  // type: true,
};

export const AppServicesTabEndpoints = ({ id }: { id: string }) => {
  const api = useApi();
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    setRowSelectionModel(newSelection);
  };
  const [selectedItemId, setSelectedItemId] = useState<string>('');
  const selectedRowItem = useMemo(() => {
    return rowSelectionModel.length === 1 ? rowSelectionModel[0].toString() : '';
  }, [rowSelectionModel]);

  useEffect(() => {
    if (rowSelectionModel.length === 1) {
      setSelectedItemId(selectedRowItem);
    } else {
      setSelectedItemId('');
    }
  }, [rowSelectionModel, selectedRowItem]);

  const [showContacts, setShowContacts] = useState(false);

  const { isLoading, data, refetch } = useQuery(
    ['appServiceEndpoints'],
    api.getAppServiceEndpointsApiV1GetAppServiceEndpointsGet.bind(null, {
      app_service_id: id,
    }),
    { enabled: true, cacheTime: 0 },
  );
  // const { isLoading, data, refetch } = useQuery(
  //   ['appServiceNotifications'],
  //   api.getAppServiceNotificationsApiV1GetAppServiceNotificationsGet.bind(null, {
  //     app_service_id: id,
  //   }),
  //   { enabled: true, cacheTime: 0 },
  // );

  const handleDataRefetch = () => {
    refetch();
    toast.success('Refreshed data.');
  };

  const approveEndpoint = useMutation(api.approveEpApiV1ApproveEndpointPost, {
    onSuccess() {
      toast.success('Endpoint approved.');
      refetch();
    },
    onError: apiError,
  });

  const handleApprove = () => {
    approveEndpoint.mutate({
      ep_id: selectedItemId,
    });
  };

  const removeAppService = useMutation(api.rejectEpApiV1RejectEndpointPost, {
    onSuccess() {
      toast.success('Endpoint rejected.');
      refetch();
    },
    onError: apiError,
  });

  const handleReject = () => {
    removeAppService.mutate({
      ep_id: selectedItemId,
    });
  };

  if (isLoading) {
    return <Loading />;
  }

  if (id === undefined || !data || data?.data.length === 0) {
    return <NoData />;
  }

  return (
    <>
      <DataTableGrid
        rows={
          data?.data.map((item) => {
            return {
              ...item,
              id: item.ep_id,
            };
          }) || []
        }
        columns={columns}
        handleSelectionChange={handleSelectionChange}
        rowSelectionModel={rowSelectionModel}
        itemsSelected={rowSelectionModel.length === 1}
        columnVisibilityModel={columnVisible}
        refreshData={handleDataRefetch}
        menuItems={[
          {
            label: 'Approve',
            icon: <ThumbUpAltOutlinedIcon color="success" fontSize="small" />,
            onClick: handleApprove,
          },
          {
            label: 'Reject',
            icon: <ThumbDownAltOutlinedIcon color="error" fontSize="small" />,
            onClick: handleReject,
          },
          {
            label: 'Display Contacts',
            icon: <PermContactCalendarOutlinedIcon color="info" fontSize="small" />,
            onClick: () => setShowContacts(true),
          },
        ]}
      />
      {showContacts && selectedItemId && (
        <DialogStandard
          open={showContacts}
          handleClose={() => setShowContacts(false)}
          title={`Contacts for Endpoint ID: ${selectedItemId}`}
        >
          <DisplayContacts id={selectedItemId || ''} />
        </DialogStandard>
      )}
    </>
  );
};
